import type { UseFormReturn } from 'react-hook-form';
import type { TabsProps } from 'antd';
import { t } from 'i18next';
import Availability from 'pos-client/components/locations/routes/components/modals/AvailabilityTabContent.tsx';
import BasicDetails from 'pos-client/components/locations/routes/components/modals/BasicDetailsTabContent.tsx';
import Capacity from 'pos-client/components/locations/routes/components/modals/CapacityTabContent.tsx';
import RechargeSettings from 'pos-client/components/locations/routes/components/modals/RechargeSettingsTabContent.tsx';
import type { RouteModel } from 'pos-shared/models/resourceManagementSchema.ts';
import type {
  RouteAvailabilityFormModel,
  RouteBasicDetailsModel,
  RouteCapacityModel,
  RouteRechargeSettingsModel,
} from 'pos-shared/models/routeSchema.ts';
export interface Step {
  key: number;
  name: string;
  isValid: boolean;
  stepSaved: boolean;
}

interface RoutesTabsArgs {
  basicDetailsForm: UseFormReturn<RouteBasicDetailsModel>;
  availabilityForm: UseFormReturn<RouteAvailabilityFormModel>;
  rechargeSettingsForm: UseFormReturn<RouteRechargeSettingsModel>;
  capacityForm?: UseFormReturn<RouteCapacityModel>;
  allSteps: Step[];
  isEdit?: boolean;
  route?: RouteModel;
}
export const createRouteTabs = ({
  basicDetailsForm,
  availabilityForm,
  rechargeSettingsForm,
  capacityForm,
  allSteps,
  isEdit,
  route,
}: RoutesTabsArgs): TabsProps['items'] => {
  isEdit = true;

  const tabs = [
    {
      key: '0',
      label: `${!isEdit ? '1. ' : ''}${t('common.contact.basicDetails')}`,
      children: <BasicDetails form={basicDetailsForm} route={route} />,
    },
    {
      key: '1',
      label: `${!isEdit ? '2. ' : ''}${t(
        'scheduling.availability.availability'
      )}`,
      children: <Availability form={availabilityForm} />,
      disabled: isEdit ? false : !allSteps[0].stepSaved,
    },
    {
      key: '2',
      label: `${!isEdit ? '3. ' : ''}${t(
        'routes.rechargeSettings.rechargeSettings'
      )}`,
      children: <RechargeSettings form={rechargeSettingsForm} route={route} />,
      disabled: isEdit ? false : !allSteps[1].stepSaved,
    },
    {
      key: '3',
      label: `${!isEdit ? '4. ' : ''}${t('routes.capacity')}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children: <Capacity form={capacityForm as any} route={route} />,
      disabled: isEdit ? false : !allSteps[2].stepSaved,
    },
  ];
  return tabs;
};

interface RouteInitialStepsArgs {
  basicDetailsForm: UseFormReturn<RouteBasicDetailsModel>;
  availabilityForm: UseFormReturn<RouteAvailabilityFormModel>;
  rechargeSettingsForm: UseFormReturn<RouteRechargeSettingsModel>;
  capacityForm?: UseFormReturn<RouteCapacityModel>;
}

export const createRouteInitialSteps = ({
  basicDetailsForm,
  availabilityForm,
  rechargeSettingsForm,
  capacityForm,
}: RouteInitialStepsArgs): Step[] => {
  const steps: Step[] = [
    {
      key: 0,
      name: 'basicDetails',
      isValid: basicDetailsForm.formState.isValid,
      stepSaved: false,
    },
    {
      key: 1,
      name: 'availability',
      isValid: availabilityForm.formState.isValid,
      stepSaved: false,
    },
    {
      key: 2,
      name: 'rechargeSettings',
      isValid: rechargeSettingsForm.formState.isValid,
      stepSaved: false,
    },
  ];

  if (capacityForm) {
    steps.push({
      key: 3,
      name: 'capacity',
      isValid: capacityForm.formState.isValid,
      stepSaved: false,
    });
  }

  return steps;
};
